import { supabase } from '../lib/supabase';
import { WebhookSettings } from '../hooks/useWebhook';
import { JsonObject } from '../types/json';

type ContactType = 'email' | 'phone';

interface ContactInfo {
  id: string;
  type: ContactType;
  value: string;
  message_id: string;
  created_at: string;
  conversation_id: string;
  chatbot_public_id: string;
}

interface ConversationMessage {
  content: string;
  role: string;
  created_at: string;
  message_id: string;
}

interface WebhookPayload {
  event: 'new_lead';
  chatbot_id: string;
  timestamp: string;
  data: {
    contact_info: {
      type: ContactType;
      value: string;
    };
    source: string;
    timestamp: string;
    metadata?: Record<string, unknown>;
    conversation?: {
      id: string;
      messages: Array<{
        content: string;
        role: string;
        timestamp: string;
      }>;
    };
  };
}

/**
 * Send contact info to the webhook endpoint
 */
export async function sendLeadToWebhook(
  contactInfo: ContactInfo,
  settings: WebhookSettings,
  provider: 'zapier' | 'make'
): Promise<boolean> {
  if (!settings.enabled || !settings.url || !contactInfo) {
    console.error('Webhook not configured or missing contact info');
    return false;
  }

  try {
    // Fetch conversation messages for context
    const { data: messages, error: messagesError } = await supabase
      .from('conversation_messages')
      .select('content, role, created_at, message_id')
      .eq('conversation_id', contactInfo.conversation_id)
      .order('sequence', { ascending: true });

    if (messagesError) {
      console.error('Error fetching conversation messages:', messagesError);
    }

    // Prepare webhook payload
    const payload: WebhookPayload = {
      event: 'new_lead',
      chatbot_id: contactInfo.chatbot_public_id,
      timestamp: new Date().toISOString(),
      data: {
        contact_info: {
          type: contactInfo.type,
          value: contactInfo.value
        },
        source: 'chatbot',
        timestamp: contactInfo.created_at,
        metadata: {
          lead_id: contactInfo.id
        }
      }
    };

    // Add conversation data if available
    if (messages && messages.length > 0) {
      payload.data.conversation = {
        id: contactInfo.conversation_id,
        messages: messages.map(msg => ({
          content: msg.content,
          role: msg.role,
          timestamp: msg.created_at
        }))
      };
    }

    // Send the webhook request
    const response = await fetch(settings.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [`X-${provider}-Token`]: settings.token || ''
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Webhook request failed with status: ${response.status}`);
    }

    // Log the webhook activity
    await logWebhookActivity(
      contactInfo.chatbot_public_id,
      provider,
      'lead',
      true,
      {
        lead_id: contactInfo.id,
        request: payload,
        response_status: response.status
      }
    );

    // Mark the contact as processed
    await supabase
      .from('conversation_contact_info')
      .update({ processed: true })
      .eq('id', contactInfo.id);

    return true;
  } catch (error) {
    console.error('Error sending lead to webhook:', error);

    // Log the failed attempt
    await logWebhookActivity(
      contactInfo.chatbot_public_id,
      provider,
      'lead',
      false,
      {
        lead_id: contactInfo.id,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    );

    return false;
  }
}

/**
 * Log webhook activity for auditing and debugging
 */
async function logWebhookActivity(
  chatbotPublicId: string,
  provider: string,
  type: 'lead' | 'order',
  success: boolean,
  details: Record<string, unknown>
) {
  try {
    // Create a properly typed payload
    const payload: JsonObject = {
      chatbot_public_id: chatbotPublicId,
      provider,
      type,
      details: details as JsonObject
    };

    await supabase.from('webhook_events').insert({
      user_id: 'system', // Required field
      event_type: `${type}_webhook_${success ? 'success' : 'failure'}`,
      status: success ? 'completed' : 'failed',
      payload
    });
  } catch (err) {
    console.warn('Error logging webhook activity:', err);
  }
}

/**
 * Process all unprocessed leads for a specific chatbot and send them to webhooks
 */
export async function processUnsentLeads(chatbotPublicId: string) {
  try {
    // Get the webhook configuration
    const { data: webhookConfig, error: configError } = await supabase
      .from('chatbot_integrations')
      .select('config, integration_id')
      .eq('chatbot_public_id', chatbotPublicId)
      .eq('integration_id', 'lead_webhook')
      .single();

    if (configError || !webhookConfig || !webhookConfig.config) {
      console.log('No webhook configuration found for chatbot', chatbotPublicId);
      return;
    }

    const settings = webhookConfig.config as unknown as WebhookSettings;
    if (!settings.enabled || !settings.url) {
      console.log('Webhook not enabled or missing URL for chatbot', chatbotPublicId);
      return;
    }

    // Determine provider from URL
    const provider = settings.url.includes('zapier') ? 'zapier' : 'make';

    // Get unprocessed leads
    const { data: leads, error: leadsError } = await supabase
      .from('conversation_contact_info')
      .select('*')
      .eq('chatbot_public_id', chatbotPublicId)
      .eq('processed', false);

    if (leadsError) {
      console.error('Error fetching unprocessed leads:', leadsError);
      return;
    }

    if (!leads || leads.length === 0) {
      console.log('No unprocessed leads found for chatbot', chatbotPublicId);
      return;
    }

    // Process each lead
    for (const lead of leads) {
      await sendLeadToWebhook(lead as ContactInfo, settings, provider);
    }

    console.log(`Processed ${leads.length} leads for chatbot ${chatbotPublicId}`);
  } catch (error) {
    console.error('Error processing unprocessed leads:', error);
  }
} 